import React, { useState, useEffect, useRef } from "react";
import Modal from "react-modal";
import "../css/SelectJobModal.css";
import { v4 as uuidv4 } from "uuid";
import AddressEndpointFields, { addressSnapshotFields, emptyAddressEndpoint, endpointFromAddress, isCompleteAddress } from './AddressEndpointFields';
import { downloadNetworkFreightTemplate, uploadNetworkFreightCsv } from '../../api/route';

const SelectJobModal = ({
  theme,
  openSelectJob,
  setOpenSelectJob,
  customers,
  customerAccounts,
  addressBook,
  addJob,
  addBulkJobs, // New prop for bulk job addition
  date,
  routes,
  syncJobs, // New prop for syncing jobs
  canSaveAddress,
  onAddressSaved,
  onNetworkFreightUploaded,
}) => {
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [selectedTemplateKey, setSelectedTemplateKey] = useState("");
  const [collection, setCollection] = useState(emptyAddressEndpoint());
  const [delivery, setDelivery] = useState(emptyAddressEndpoint());
  const [route, setRoute] = useState("");
  const [jobComments, setJobComments] = useState("");
  const [error, setError] = useState("");
  const [todaysRoutes, setTodaysRoutes] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [showList, setShowList] = useState(false);
  
  // New states for bulk mode
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedJobs, setSelectedJobs] = useState([]);
  const [networkMode, setNetworkMode] = useState(false);
  const [networkFile, setNetworkFile] = useState(null);
  const [isUploadingNetwork, setIsUploadingNetwork] = useState(false);
  const dropdownRef = useRef(null);

  // Filter routes for the selected date
  useEffect(() => {
    const todays = routes.filter((r) => r.date === date);
    setTodaysRoutes(todays);
  }, [routes, date]);

  // Initialize filtered customers list
  useEffect(() => {
    setFilteredCustomers(customers ? Object.entries(customers) : []);
  }, [customers]);

  // Filter customers by search term
  useEffect(() => {
    if (!searchTerm) {
      setFilteredCustomers(Object.entries(customers));
    } else {
      const filtered = Object.entries(customers).filter(([_, d]) =>
        (d.customer_company || d.company_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (d.route_name || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredCustomers(filtered);
    }
  }, [searchTerm, customers]);

  // Handle click outside dropdown in bulk mode
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (bulkMode && dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowList(false);
      }
    };

    if (bulkMode && showList) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [bulkMode, showList]);

  // Check if route exists in today's routes
  const routeExists = (route) => todaysRoutes.some((r) => r.route === route);

  const selectedCustomer = customerAccounts.find((customer) => String(customer.id) === String(selectedCustomerId));
  const selectedCompanyKey = (selectedCustomer?.company || '').trim().toLowerCase();
  const customerTemplates = Object.entries(customers).filter(([_, template]) =>
    (template.customer_company || '').trim().toLowerCase() === selectedCompanyKey
  );
  const matchingCustomerAccounts = customerAccounts.filter((customer) =>
    (customer.company || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelectCustomer = (customer) => {
    setSelectedCustomerId(String(customer.id));
    setSelectedTemplateKey("");
    setCollection(emptyAddressEndpoint());
    setDelivery(emptyAddressEndpoint());
    setRoute("");
    setJobComments("");
    setSearchTerm(customer.company || "");
    setShowList(false);
    setError("");
  };

  const handleSelectTemplate = (key) => {
    setSelectedTemplateKey(key);
    if (key === "") {
      setCollection(emptyAddressEndpoint());
      setDelivery(emptyAddressEndpoint());
      setRoute("");
      setJobComments("");
      return;
    }
    const deliveryTemplate = customers[key];
    setCollection(endpointFromAddress(deliveryTemplate.collection_address_details));
    setDelivery(deliveryTemplate.delivery_address_details
      ? endpointFromAddress(deliveryTemplate.delivery_address_details)
      : {
          address_book_id: deliveryTemplate.delivery_address_id || null,
          name: deliveryTemplate.company_name || deliveryTemplate.customer_company || '',
          address: deliveryTemplate.address || '',
          postcode: deliveryTemplate.postcode || '',
          additional_info: '',
        });
    setRoute(deliveryTemplate.route_name || "");
    setJobComments(deliveryTemplate.delivery_instructions || "");
  };

  // Toggle job selection for bulk mode
  const toggleJobSelection = (customerId) => {
    setSelectedJobs(prev => {
      if (prev.includes(customerId)) {
        return prev.filter(id => id !== customerId);
      } else {
        return [...prev, customerId];
      }
    });
  };

  // Add job handler (single mode)
  const handleAddJob = () => {
    if (!selectedCustomerId || !selectedCustomer) {
      setError("Please select a customer.");
      return;
    }
    if (!route.trim()) {
      setError("Route is required.");
      return;
    }
    if (!isCompleteAddress(collection) || !isCompleteAddress(delivery)) {
      setError("Complete collection and delivery addresses are required.");
      return;
    }

    setError("");
    const selectedTemplate = selectedTemplateKey !== '' ? customers[selectedTemplateKey] : null;
    const snapshots = addressSnapshotFields(collection, delivery);
    const jobData = {
          ...snapshots,
          job: snapshots.delivery_name,
          address: snapshots.delivery_address,
          postcode: snapshots.delivery_postcode,
          route: route.trim(),
          date,
          profile_id: null,
          route_uuid: uuidv4(),
          locked: false,
          accepted: false,
          completed: false,
          img: null,
          driver_comments: null,
          job_comments: selectedTemplate?.delivery_instructions || null,
          confirmed: false,
          costings: selectedTemplate?.price ?? null,
          invoice_id: selectedTemplate?.invoice_customer_id ?? null,
          account_ref: selectedTemplate?.account_ref ?? selectedCustomer?.account_ref ?? "CF0001",
          customer_id: selectedCustomer.id,
          customer_deliveries_id: selectedTemplate?.id ?? null,
          customer_company: selectedCustomer.company,
        };

    addJob(jobData);
    resetModalState();
    setOpenSelectJob(false);
  };

  // Add bulk jobs handler
  const handleAddBulkJobs = () => {
    if (selectedJobs.length === 0) {
      setError("Please select at least one job to add.");
      return;
    }

    setError("");
    const jobsData = selectedJobs.map(deliveryKey => {
      const deliveryTemplate = customers[deliveryKey];
      const collectionEndpoint = endpointFromAddress(deliveryTemplate.collection_address_details);
      const deliveryEndpoint = deliveryTemplate.delivery_address_details
        ? endpointFromAddress(deliveryTemplate.delivery_address_details)
        : {
            address_book_id: deliveryTemplate.delivery_address_id || null,
            name: deliveryTemplate.company_name || deliveryTemplate.customer_company || '',
            address: deliveryTemplate.address || '',
            postcode: deliveryTemplate.postcode || '',
            additional_info: '',
          };
      const snapshots = addressSnapshotFields(collectionEndpoint, deliveryEndpoint);
      return {
        ...snapshots,
        job: snapshots.delivery_name,
        address: snapshots.delivery_address,
        postcode: snapshots.delivery_postcode,
        route: deliveryTemplate.route_name || null,
        date,
        profile_id: null,
        route_uuid: uuidv4(),
        locked: false,
        accepted: false,
        completed: false,
        img: null,
        driver_comments: null,
        job_comments: deliveryTemplate.delivery_instructions || null,
        confirmed: false,
        costings: deliveryTemplate.price ?? null,
        invoice_id: deliveryTemplate.invoice_customer_id ?? null,
        account_ref: deliveryTemplate.account_ref ?? "CF0001",
        customer_id: deliveryTemplate.customer_id ?? null,
        customer_deliveries_id: deliveryTemplate.id ?? null,
        customer_company: deliveryTemplate.customer_company ?? null,
      };
    });

    addBulkJobs(jobsData);
    resetModalState();
    setOpenSelectJob(false);
  };

  const handleNetworkUpload = async () => {
    if (!selectedCustomerId) {
      setError('Please select the customer that owns this freight.');
      return;
    }
    if (!networkFile) {
      setError('Please select a CSV file.');
      return;
    }
    setError('');
    setIsUploadingNetwork(true);
    const result = await uploadNetworkFreightCsv(networkFile, selectedCustomerId);
    setIsUploadingNetwork(false);
    const createdJobs = result.data?.created_jobs || [];
    if (createdJobs.length) onNetworkFreightUploaded?.(createdJobs);
    if (result.status !== 200) {
      const validationErrors = result.data?.errors?.length ? result.data.errors.join(' ') : '';
      const details = validationErrors || result.data?.details;
      setError(`${result.data?.error || 'Some network freight jobs could not be imported.'}${details ? ` ${details}` : ''}`);
      return;
    }
    resetModalState();
    setOpenSelectJob(false);
  };

  // Reset modal state
  const resetModalState = () => {
    setSelectedCustomerId("");
    setSelectedTemplateKey("");
    setCollection(emptyAddressEndpoint());
    setDelivery(emptyAddressEndpoint());
    setRoute("");
    setJobComments("");
    setSearchTerm("");
    setShowList(false);
    setError("");
    setBulkMode(false);
    setSelectedJobs([]);
    setNetworkMode(false);
    setNetworkFile(null);
    setIsUploadingNetwork(false);
  };

  // Submit form on Enter key
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (networkMode) {
        handleNetworkUpload();
      } else if (bulkMode) {
        handleAddBulkJobs();
      } else {
        handleAddJob();
      }
    }
  };
  
  const handleSyncJobs = () => {
      syncJobs();
  }

  return (
    <Modal
      id={theme}
      isOpen={openSelectJob}
      onRequestClose={() => {
        resetModalState();
        setOpenSelectJob(false);
      }}
      ariaHideApp={false}
      style={{
        overlay: {
          zIndex: 99999,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        },
        content: {
          inset: "16px",
          padding: 0,
          margin: "auto",
          justifyContent: "center",
          display: "flex",
          backgroundColor: "transparent",
          border: "none",
          zIndex: 100000,
        },
      }}
    >
      <form onKeyDown={handleKeyPress} style={{ width: '100%', maxWidth: '1200px' }}>
        <div className={'job-modal-container'}>
          <div className="flex items-center justify-between" style={{ marginTop: '20px', marginBottom: '20px' }}>
            <h2 style={{ margin: 0 }}>{networkMode ? 'Upload Network Freight' : 'Select Job'}</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={() => {
                  setNetworkMode((current) => !current);
                  setBulkMode(false);
                  setSelectedJobs([]);
                  setError('');
                }}
              >
                {networkMode ? 'Manual Add' : 'Upload CSV'}
              </button>
              {!networkMode && <div className="bulk-mode-toggle">
              <input
                type="checkbox"
                id="bulkMode"
                checked={bulkMode}
                onChange={(e) => {
                  setBulkMode(e.target.checked);
                  if (!e.target.checked) {
                    setSelectedJobs([]); // Clear selected jobs when turning off bulk mode
                  }
                }}
                className="bulk-mode-checkbox"
              />
              <label htmlFor="bulkMode" className="bulk-mode-label">
                Bulk Add Mode
              </label>
              </div>}
            </div>
          </div>

          <div className="select-group" style={{ position: "relative" }} ref={dropdownRef}>
            <label htmlFor="customerSearch">{bulkMode ? 'Choose saved routes:' : networkMode ? 'Choose the customer for this upload *' : 'Choose a customer *'}</label>
            <div style={{ position: "relative" }}>
              <input
                id="customerSearch"
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  if (!bulkMode) {
                    setSelectedCustomerId("");
                    setSelectedTemplateKey("");
                    setCollection(emptyAddressEndpoint());
                    setDelivery(emptyAddressEndpoint());
                    setRoute("");
                  }
                  setShowList(true);
                }}
                placeholder="Start typing company name..."
                autoComplete="off"
                onFocus={() => setShowList(true)}
                onBlur={() => {
                  // In bulk mode, don't auto-close to allow multiple selections
                  if (!bulkMode) {
                    setTimeout(() => setShowList(false), 150);
                  }
                }}
                onKeyDown={(e) => {
                  // Close dropdown on Escape key in bulk mode
                  if (e.key === "Escape" && bulkMode) {
                    setShowList(false);
                  }
                }}
                className="input"
              />
              {bulkMode && showList && (
                <button
                  type="button"
                  onClick={() => setShowList(false)}
                  style={{
                    position: "absolute",
                    right: "8px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    fontSize: "16px",
                    cursor: "pointer",
                    color: "#666",
                    padding: "4px"
                  }}
                >
                  ×
                </button>
              )}
            </div>
            {showList && (bulkMode ? filteredCustomers.length > 0 : matchingCustomerAccounts.length > 0) && (
              <ul className="dropdown-list">
                {(bulkMode
                  ? filteredCustomers
                      .filter(([_, d]) => d.route_name)
                      .sort((a, b) => {
                        const companyCompare = (a[1].customer_company || '').localeCompare(b[1].customer_company || '');
                        if (companyCompare !== 0) return companyCompare;
                        return (a[1].route_name || '').localeCompare(b[1].route_name || '');
                      })
                    : matchingCustomerAccounts.map((customer) => [String(customer.id), customer])
                ).map(([key, d]) => (
                    <li
                      key={key}
                      onMouseDown={() => bulkMode ? toggleJobSelection(key) : handleSelectCustomer(d)}
                      className="dropdown-item"
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        backgroundColor: bulkMode && selectedJobs.includes(key) ? '#fce30775' : 'transparent'
                      }}
                    >
                      <span style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                        <span style={{ fontWeight: 600 }}>
                          {bulkMode ? `${routeExists(d.route_name) ? "✅" : "🔴"} ${d.customer_company}` : d.company}
                        </span>
                        {bulkMode && <span style={{ fontSize: '0.82em', opacity: 0.75, paddingLeft: '22px' }}>
                          {d.company_name && d.company_name !== d.customer_company ? `${d.company_name} · ` : ''}{d.route_name}
                        </span>}
                      </span>
                      {bulkMode && (
                        <input
                          type="checkbox"
                          checked={selectedJobs.includes(key)}
                          onChange={() => toggleJobSelection(key)}
                          className="ml-2"
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}
                    </li>
                  ))}
              </ul>
            )}
          </div>

          {!bulkMode && !networkMode && (
            <div className="manual-inputs job-address-grid">
              <div className="route-input-group">
                <label htmlFor="savedRoute">Saved route (optional):</label>
                <select id="savedRoute" className="input" value={selectedTemplateKey} onChange={(event) => handleSelectTemplate(event.target.value)} disabled={!selectedCustomerId}>
                  <option value="">Enter route and addresses manually</option>
                  {customerTemplates.map(([key, template]) => (
                    <option key={template.id} value={key}>{template.route_name}</option>
                  ))}
                </select>
              </div>

              <AddressEndpointFields label="Collection" addresses={addressBook} value={collection} onChange={setCollection} disabled={!selectedCustomerId || selectedTemplateKey !== ''} canSaveAddress={canSaveAddress} onAddressSaved={onAddressSaved} />
              <AddressEndpointFields label="Delivery" addresses={addressBook} value={delivery} onChange={setDelivery} disabled={!selectedCustomerId || selectedTemplateKey !== ''} canSaveAddress={canSaveAddress} onAddressSaved={onAddressSaved} />

              <div className="route-input-group">
                <label htmlFor="route">Route:</label>
                <input
                  id="route"
                  type="text"
                  className="input"
                  value={route}
                  onChange={(e) => setRoute(e.target.value)}
                  disabled={!selectedCustomerId || selectedTemplateKey !== ''}
                  required
                />
              </div>
            </div>
          )}

          {networkMode && (
            <div className="selected-jobs-container" style={{ marginTop: 18 }}>
              <label htmlFor="networkFreightCsv"><strong>Network freight CSV *</strong></label>
              <input
                id="networkFreightCsv"
                type="file"
                accept=".csv,text/csv"
                className="input"
                onChange={(event) => setNetworkFile(event.target.files?.[0] || null)}
              />
              <button type="button" className="btn btn-outline-secondary" onClick={downloadNetworkFreightTemplate} style={{ marginTop: 12 }}>
                Download CSV Template
              </button>
            </div>
          )}

          {bulkMode && selectedJobs.length > 0 && (
            <div className="selected-jobs-container">
              <h3 className="selected-jobs-title">
                Selected Jobs ({selectedJobs.length}):
              </h3>
              <div className="selected-jobs-list">
                {selectedJobs.map(deliveryKey => (
                  <div key={deliveryKey} className="selected-job-item">
                    <span>
                      {customers[deliveryKey]?.customer_company} — {customers[deliveryKey]?.company_name || customers[deliveryKey]?.route_name}
                    </span>
                    <button 
                      type="button" 
                      onClick={() => toggleJobSelection(deliveryKey)}
                      className="selected-job-remove-btn"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {error && <p className="error-message">{error}</p>}

          <div className="button-group" style={{ display: 'flex', gap: '10px', marginTop: '20px', width: '100%' }}>
              {!networkMode && <button
                type="button"
                className="add-button"
                style={{ backgroundColor: '#00a2ff',  marginRight: 'auto' }}
                onClick={handleSyncJobs}
              >
                Sync Daily Jobs
              </button>}
            {networkMode ? (
              <button type="button" className="add-button" onClick={handleNetworkUpload} disabled={!selectedCustomerId || !networkFile || isUploadingNetwork}>
                {isUploadingNetwork ? 'Uploading...' : 'Upload CSV'}
              </button>
            ) : bulkMode ? (
              <button 
                type="button" 
                className="add-button" 
                onClick={handleAddBulkJobs}
                disabled={selectedJobs.length === 0}
              >
                Add {selectedJobs.length} Job{selectedJobs.length !== 1 ? 's' : ''}
              </button>
            ) : (
              <button type="button" className="add-button" onClick={handleAddJob}>
                Add Job
              </button>
            )}
            <button
              type="button"
              className="close-button"
              onClick={() => {
                resetModalState();
                setOpenSelectJob(false);
              }}
            >
              Close
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default SelectJobModal;
